/*
 * Fernhollow Square — wire protocol.
 *
 * This file is the single source of truth for messages exchanged between the
 * browser client and the PartyKit server. Both sides import from /shared, so
 * this file must stand alone — DO NOT import anything from /client/src or
 * /server/src here.
 *
 * Companion doc: /docs/NETWORKING.md (read it for the why; this file is the what).
 *
 * Design notes:
 *  - Every message is a discriminated union member with a string `type`.
 *  - All client→server message types are prefixed `client_`.
 *  - All server→client message types are prefixed `server_`.
 *  - Chat content on the wire is ALWAYS a phrase index into PHRASES — never free
 *    text. This is a load-bearing moderation decision (see CLAUDE.md).
 *  - Cosmetic and species choices travel as integer indices into the client-side
 *    content catalogs (FUR_COLORS, HATS). The server validates the range but
 *    has no opinion on what each index means visually.
 *  - The wire types intentionally do NOT mirror the in-memory `Critter` shape
 *    one-to-one: we strip transient render fields (bubbleExpiresAt, tx/ty) and
 *    encode chat as `phrase` indices instead of strings.
 *
 * Versioning:
 *  - `PROTOCOL_VERSION` is bumped on any breaking change.
 *  - Server compares against `client_hello.protocolVersion` and either welcomes
 *    or sends a `server_error` of kind 'version_mismatch' before closing the
 *    socket.
 */

/** Bump on any breaking change to the message shape or semantics. */
export const PROTOCOL_VERSION = 1;

/** Server-enforced cap on critters per room. */
export const ROOM_CAPACITY = 16;

/** Position update cadence the client SHOULD send while moving (in Hz). */
export const POSITION_UPDATE_HZ = 10;

/** Grace window during which a disconnected critter's slot is held (ms). */
export const DISCONNECT_GRACE_MS = 30_000;

/** Heartbeat: client SHOULD ping at least this often; server may disconnect otherwise (ms). */
export const HEARTBEAT_INTERVAL_MS = 15_000;

/** Max chat bubble lifetime, server-broadcast and client-displayed (ms). */
export const CHAT_BUBBLE_TTL_MS = 4_000;

/** How long an emote pose holds before auto-clearing (ms). */
export const EMOTE_TTL_MS = 3_000;

/**
 * Known emote names. The wire only carries these strings; rendering them is the
 * client's concern (see `client/src/content/emotes.ts`).
 *
 * Adding a new emote: append to this union AND to the EMOTE_NAMES array.
 * Reordering is fine — these are strings, not indices.
 */
export type EmoteName = 'wave' | 'dance' | 'sit';

/** Runtime list mirroring the EmoteName union — used for server-side validation. */
export const EMOTE_NAMES: ReadonlyArray<EmoteName> = ['wave', 'dance', 'sit'];

export function isEmoteName(s: unknown): s is EmoteName {
  return typeof s === 'string' && (EMOTE_NAMES as ReadonlyArray<string>).includes(s);
}

/* ──────────────────────────────────────────────────────────────────────────
 * Wire types
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Server-issued stable identifier for a critter inside a room.
 * Opaque string (PartyKit connection id, or a derivative).
 */
export type CritterId = string;

/**
 * Server-issued session token. Returned in `server_welcome` and presented in
 * `client_hello.sessionToken` on reconnect to recover the same `CritterId`
 * (and held position) within the `DISCONNECT_GRACE_MS` window.
 */
export type SessionToken = string;

/** Index into the client's PHRASES catalog. Server validates `0 <= i < phraseCount`. */
export type PhraseIndex = number;

/** Index into FUR_COLORS. Server validates against a server-known bound. */
export type FurIndex = number;

/** Index into HATS. Server validates against a server-known bound. */
export type HatIndex = number;

/**
 * Presence state for a remote critter.
 *  - 'active' — currently connected and present in the room
 *  - 'idle'   — recently disconnected; the slot is being held for the
 *               {@link DISCONNECT_GRACE_MS} window. Client renders these as a
 *               grey-out placeholder for now; the longer-term plan is to swap
 *               in a non-critter visual (an egg, a flower, etc.) the player
 *               can also customize. See NETWORKING.md §"Presence" for detail.
 */
export type CritterPresence = 'active' | 'idle';

/**
 * Wire-friendly subset of the in-memory `Critter`. Transient render state
 * (movement target, bubble-expiry timestamp) is not transmitted; the server
 * sends position snapshots and chat events separately.
 *
 * `neck` index is reserved but not transmitted today (the structural slot
 * exists in client SaveData; once neck items ship, bump PROTOCOL_VERSION and
 * add the field here).
 */
export interface WireCritter {
  id: CritterId;
  name: string;
  x: number;
  y: number;
  color: FurIndex;
  hat: HatIndex;
  /** Defaults to 'active' for fresh joins; transitions to 'idle' on grace-window disconnect. */
  state: CritterPresence;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Client → Server
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * First message sent on a new socket. The server replies with `server_welcome`
 * (success) or `server_error` (rejected) before any other traffic.
 *
 * On reconnect within the grace window, set `sessionToken` to the value from
 * the prior `server_welcome` to recover the same critter slot/position.
 */
export interface ClientHello {
  type: 'client_hello';
  protocolVersion: number;
  /** Cosmetic + identity payload the client claims. Server validates indices. */
  critter: {
    name: string;
    color: FurIndex;
    hat: HatIndex;
  };
  /** Present iff reconnecting within DISCONNECT_GRACE_MS. */
  sessionToken?: SessionToken;
}

/**
 * Position snapshot. Client sends at ~10 Hz while moving; may go silent when
 * idle. The server treats the client as authoritative for its own position
 * (cozy game, low stakes) but clamps to room bounds and rate-limits.
 */
export interface ClientMove {
  type: 'client_move';
  x: number;
  y: number;
  /** Client-side monotonic timestamp (ms) — used only for ordering, not trust. */
  t: number;
}

/**
 * Player chose a phrase from the safelist. Server validates the index and
 * re-broadcasts to the room (including the sender, for echo simplicity).
 */
export interface ClientChat {
  type: 'client_chat';
  phrase: PhraseIndex;
}

/**
 * Player changed fur color and/or hat in the customizer. Both fields are sent
 * even if only one changed — keeps the server's state replacement trivial.
 */
export interface ClientCosmeticUpdate {
  type: 'client_cosmetic_update';
  color: FurIndex;
  hat: HatIndex;
}

/**
 * Player finished a minigame run. The server records it for leaderboard
 * purposes (subject to rate-limit + sanity bounds — see NETWORKING.md
 * §Cheat resistance) and broadcasts a `server_leaderboard_update` if the
 * leaderboard changed.
 *
 * `score` is bounded per-minigame on the server; out-of-range scores are
 * dropped silently (no error to the user — cozy tone).
 */
export interface ClientMinigameScore {
  type: 'client_minigame_score';
  minigame: 'forage' | 'fish';
  score: number;
}

/** Heartbeat. Server replies with `server_pong` carrying the same `t`. */
export interface ClientPing {
  type: 'client_ping';
  t: number;
}

/**
 * Player triggered an emote (wave / dance / sit). Server validates the name
 * against EMOTE_NAMES and re-broadcasts to the room (including the sender,
 * matching the echo-for-simplicity rule used by chat).
 */
export interface ClientEmote {
  type: 'client_emote';
  emote: EmoteName;
}

export type ClientMessage =
  | ClientHello
  | ClientMove
  | ClientChat
  | ClientCosmeticUpdate
  | ClientMinigameScore
  | ClientPing
  | ClientEmote;

/* ──────────────────────────────────────────────────────────────────────────
 * Server → Client
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Reply to a successful `client_hello`. The client should treat `you.id` as
 * its canonical id and keep `sessionToken` for reconnects.
 *
 * `serverTime` lets the client align bubble-expiry math with the server clock
 * without trusting its own.
 */
export interface ServerWelcome {
  type: 'server_welcome';
  protocolVersion: number;
  you: WireCritter;
  sessionToken: SessionToken;
  serverTime: number;
  /** Catalog sizes the server is willing to accept. Client should clamp. */
  bounds: {
    furCount: number;
    hatCount: number;
    phraseCount: number;
  };
}

/**
 * Initial room snapshot sent immediately after `server_welcome` (and again on
 * request if the client suspects desync). Includes every other critter the
 * client should render. The recipient is NOT included in `critters`.
 */
export interface ServerRoomState {
  type: 'server_room_state';
  roomId: string;
  critters: WireCritter[];
  serverTime: number;
}

/** A new critter joined the room (after the recipient was already in it). */
export interface ServerCritterJoined {
  type: 'server_critter_joined';
  critter: WireCritter;
}

/**
 * A critter left the room. If `reason === 'disconnect'` the slot is held for
 * `DISCONNECT_GRACE_MS`; the client should keep the critter visible for the
 * grace window (the prototype implementation may simply remove it — UX call).
 */
export interface ServerCritterLeft {
  type: 'server_critter_left';
  id: CritterId;
  reason: 'disconnect' | 'timeout' | 'kicked';
}

/**
 * Position broadcast for a remote critter. Server forwards the most recent
 * `client_move` from each non-local critter at up to `POSITION_UPDATE_HZ`.
 * Clients interpolate between the last known position and this one.
 *
 * Echoes of the recipient's own moves are NOT sent (the client owns its own
 * position).
 */
export interface ServerCritterMoved {
  type: 'server_critter_moved';
  id: CritterId;
  x: number;
  y: number;
  /** Server-stamped time (ms) — clients use this for interpolation lerp targets. */
  t: number;
}

/**
 * A chat bubble to show above `id`. The server has already validated `phrase`
 * against PHRASES.length at handshake time. Bubble expiry is `t + CHAT_BUBBLE_TTL_MS`.
 */
export interface ServerChat {
  type: 'server_chat';
  id: CritterId;
  phrase: PhraseIndex;
  t: number;
}

/** A critter's fur or hat changed. Broadcast to everyone in the room. */
export interface ServerCosmeticChanged {
  type: 'server_cosmetic_changed';
  id: CritterId;
  color: FurIndex;
  hat: HatIndex;
}

/**
 * Presence transition for a critter the client already knows about. Used
 * during the disconnect grace window to flip a critter to 'idle' without
 * removing it from the room. If the same critter reconnects within the
 * window, a follow-up message flips back to 'active'. After the window
 * expires the server emits a `server_critter_left` and the slot is freed.
 */
export interface ServerCritterStateChanged {
  type: 'server_critter_state_changed';
  id: CritterId;
  state: CritterPresence;
}

/**
 * Scope of a leaderboard payload.
 *  - 'global'  — all-time top-N across every player. Sent on connect and on
 *                change. Slow-moving.
 *  - 'session' — live ranking for the current room/session window. Sent on
 *                every score that affects the top-N for the session. Resets
 *                when the room empties or on a scheduled session boundary
 *                (server policy).
 *
 * Sending the same shape for both keeps the client simple: it dispatches on
 * `scope` and renders into one of two cached tables.
 */
export type LeaderboardScope = 'global' | 'session';

/**
 * Leaderboard refresh. Sent on connect (snapshot) and on change. Top-N per
 * minigame, where N is a server policy (suggested: 10).
 */
export interface ServerLeaderboardUpdate {
  type: 'server_leaderboard_update';
  scope: LeaderboardScope;
  forage: LeaderboardEntry[];
  fish: LeaderboardEntry[];
}

export interface LeaderboardEntry {
  name: string;
  score: number;
  /** Server-stamped achievement time, for tiebreaks/UI. */
  t: number;
}

/**
 * Error envelope. The server may close the socket immediately after sending
 * a fatal error (e.g. `room_full`, `version_mismatch`).
 *
 * `message` is the cozy player-facing copy (already on-tone); the client may
 * surface it directly in a toast.
 */
export interface ServerError {
  type: 'server_error';
  kind:
    | 'version_mismatch'
    | 'room_full'
    | 'invalid_message'
    | 'invalid_phrase'
    | 'invalid_cosmetic'
    | 'rate_limited'
    | 'session_expired'
    | 'internal';
  message: string;
  /** True iff the server is about to close the socket. */
  fatal: boolean;
}

/** Heartbeat reply. `t` echoes the matching `client_ping.t`. */
export interface ServerPong {
  type: 'server_pong';
  t: number;
  serverTime: number;
}

/**
 * Broadcast that a critter is playing an emote. Auto-clears after EMOTE_TTL_MS
 * on the client side; no explicit "emote ended" message is sent.
 */
export interface ServerEmote {
  type: 'server_emote';
  id: CritterId;
  emote: EmoteName;
  t: number;
}

export type ServerMessage =
  | ServerWelcome
  | ServerRoomState
  | ServerCritterJoined
  | ServerCritterLeft
  | ServerCritterMoved
  | ServerChat
  | ServerCosmeticChanged
  | ServerCritterStateChanged
  | ServerLeaderboardUpdate
  | ServerError
  | ServerPong
  | ServerEmote;

/** Anything that may travel either direction. */
export type AnyMessage = ClientMessage | ServerMessage;

/* ──────────────────────────────────────────────────────────────────────────
 * Type guards
 *
 * Convenience predicates so the dispatch sites on both ends can be
 * exhaustively switch-checked. Each guard is a thin wrapper around a `type`
 * comparison and exists mainly to discourage stringly-typed branching.
 * ──────────────────────────────────────────────────────────────────────── */

export function isClientHello(m: AnyMessage): m is ClientHello {
  return m.type === 'client_hello';
}
export function isClientMove(m: AnyMessage): m is ClientMove {
  return m.type === 'client_move';
}
export function isClientChat(m: AnyMessage): m is ClientChat {
  return m.type === 'client_chat';
}
export function isClientCosmeticUpdate(m: AnyMessage): m is ClientCosmeticUpdate {
  return m.type === 'client_cosmetic_update';
}
export function isClientMinigameScore(m: AnyMessage): m is ClientMinigameScore {
  return m.type === 'client_minigame_score';
}
export function isClientPing(m: AnyMessage): m is ClientPing {
  return m.type === 'client_ping';
}
export function isClientEmote(m: AnyMessage): m is ClientEmote {
  return m.type === 'client_emote';
}

export function isServerWelcome(m: AnyMessage): m is ServerWelcome {
  return m.type === 'server_welcome';
}
export function isServerRoomState(m: AnyMessage): m is ServerRoomState {
  return m.type === 'server_room_state';
}
export function isServerCritterJoined(m: AnyMessage): m is ServerCritterJoined {
  return m.type === 'server_critter_joined';
}
export function isServerCritterLeft(m: AnyMessage): m is ServerCritterLeft {
  return m.type === 'server_critter_left';
}
export function isServerCritterMoved(m: AnyMessage): m is ServerCritterMoved {
  return m.type === 'server_critter_moved';
}
export function isServerChat(m: AnyMessage): m is ServerChat {
  return m.type === 'server_chat';
}
export function isServerCosmeticChanged(m: AnyMessage): m is ServerCosmeticChanged {
  return m.type === 'server_cosmetic_changed';
}
export function isServerCritterStateChanged(m: AnyMessage): m is ServerCritterStateChanged {
  return m.type === 'server_critter_state_changed';
}
export function isServerLeaderboardUpdate(m: AnyMessage): m is ServerLeaderboardUpdate {
  return m.type === 'server_leaderboard_update';
}
export function isServerError(m: AnyMessage): m is ServerError {
  return m.type === 'server_error';
}
export function isServerPong(m: AnyMessage): m is ServerPong {
  return m.type === 'server_pong';
}
export function isServerEmote(m: AnyMessage): m is ServerEmote {
  return m.type === 'server_emote';
}

/**
 * Exhaustiveness helper. Use in `switch` defaults to make new message types
 * a compile-time error rather than a runtime fall-through.
 *
 *   switch (msg.type) {
 *     case 'server_welcome': ...
 *     ...
 *     default: assertNever(msg);
 *   }
 */
export function assertNever(x: never): never {
  throw new Error(`Unhandled protocol message: ${JSON.stringify(x)}`);
}
